<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { MkApp, MkSidebar, type MkSidebarGroup } from '@motork/component-library/future/components';
import { it, en } from '@motork/component-library/future/locale';
import {
  DropdownMenuItem,
  SidebarInset,
  SidebarProvider,
  Toaster,
} from '@motork/component-library/future/primitives';
import { CheckIcon, LogOutIcon } from 'lucide-vue-next';
import { HOME_ITEM, MENU_ITEMS } from '@/menu.config';
import { setLocale } from '@/i18n';
import { logout, userDisplayName, userEmail } from '@service/keycloak/keycloak';
import { resetApolloStore } from '@service/apollo';
import { useBusyStore } from '@store/busy';

const route = useRoute();
const { t, locale } = useI18n();
const busy = useBusyStore();
const mkLocale = computed(() => (String(locale.value).startsWith('en') ? en : it));

const groups = computed<MkSidebarGroup[]>(() => [
  {
    items: [HOME_ITEM, ...MENU_ITEMS].map((item) => ({
      label: t(item.labelKey),
      icon: item.icon,
      to: item.to,
      as: RouterLink,
      isActive: item.to === '/'
        ? route.path === '/'
        : route.path === item.to || route.path.startsWith(`${item.to}-`) || route.path.startsWith(`${item.to}/`),
    })),
  },
]);

const user = computed(() => ({
  name: userDisplayName(),
  email: userEmail() || userDisplayName(),
}));

const appLocales = [
  { id: 'it-IT', label: 'Italiano' },
  { id: 'en-GB', label: 'English' },
] as const;

function localeActive(id: string) {
  return String(locale.value).slice(0, 2) === id.slice(0, 2);
}

async function onLogout() {
  await resetApolloStore();
  await logout();
}
</script>

<template>
  <MkApp :locale="mkLocale">
    <Toaster />
    <SidebarProvider>
      <MkSidebar title="LeadSpark GATT" :groups="groups" :user="user">
        <template #footer-menu>
          <DropdownMenuItem
            v-for="item in appLocales"
            :key="item.id"
            @click="setLocale(item.id)"
          >
            <CheckIcon v-if="localeActive(item.id)" class="mr-2 size-4" />
            <span v-else class="mr-2 inline-block size-4" />
            {{ item.label }}
          </DropdownMenuItem>
          <DropdownMenuItem @click="onLogout">
            <LogOutIcon class="mr-2 size-4" />
            {{ t('LABEL.logout') }}
          </DropdownMenuItem>
        </template>
      </MkSidebar>
      <SidebarInset>
        <div
          v-if="busy.isBusy"
          class="fixed inset-x-0 top-0 z-50 h-1 overflow-hidden bg-primary/20"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="t('LABEL.loading')"
        >
          <div class="h-full w-1/3 animate-[gatt-busy_1.1s_ease-in-out_infinite] bg-primary" />
        </div>
        <main class="min-h-svh p-6">
          <RouterView />
        </main>
      </SidebarInset>
    </SidebarProvider>
  </MkApp>
</template>
